import * as yup from 'yup';
import moment from 'moment';

// Phone number pattern: +1 (xxx) xxx-xxxx
const PHONE_PATTERN = /^\+1\s\([\d]{3}\)\s[\d]{3}-[\d]{4}$/;

/**
 * Base validation schemas that can be composed together.
 * These replicate the validate.js RULES object functionality.
 */
export const VALIDATION_SCHEMAS = {
  notEmpty: yup
    .mixed()
    .required('This field is required')
    .test('not-empty-string', 'This field is required', (value) => {
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      return value !== null && value !== undefined;
    }),

  email: yup.string().email('is not a valid email'),

  integer: yup.number().strict(true).integer('must be an integer'),

  phone: yup
    .string()
    .required('is required')
    .matches(PHONE_PATTERN, ({ value }) => `${value} is not a valid phone number. Format is +1 (xxx) xxx-xxxx`),

  number: yup.number().integer('must be an integer'),

  birthday: yup.mixed().test('is-18-years-old', 'You need to be at least 18 years old', (value) => {
    if (!value) return true; // Let required validation handle empty values
    const date = moment.utc(value);
    const minAge = moment.utc().subtract(18, 'years');
    return date.isSameOrBefore(minAge);
  }),
};

/**
 * Combines multiple validation schemas into one.
 * Similar to how validate.js allows combining rules with lodash.assign.
 *
 * @param {string[]} schemaNames - Array of schema names to combine (e.g., ['notEmpty', 'email'])
 * @returns {yup.Schema} - Combined yup schema
 */
export function combineSchemas(schemaNames) {
  if (!schemaNames || schemaNames.length === 0) {
    return yup.mixed();
  }

  // Start with the first schema
  let combinedSchema = VALIDATION_SCHEMAS[schemaNames[0]];

  // Add validations from subsequent schemas
  for (let i = 1; i < schemaNames.length; i++) {
    const schemaName = schemaNames[i];
    const schema = VALIDATION_SCHEMAS[schemaName];

    if (schema) {
      combinedSchema = combinedSchema.concat(schema);
    }
  }

  return combinedSchema;
}

/**
 * Validates a single field value against specified validation rules.
 * Replaces the validate.js validate() function call pattern.
 *
 * @param {string} fieldName - The name of the field being validated
 * @param {any} value - The value to validate
 * @param {string[]} validationRules - Array of rule names (e.g., ['notEmpty', 'email'])
 * @returns {string|null} - Error message string or null if valid
 */
export function validateField(fieldName, value, validationRules) {
  if (!validationRules || validationRules.length === 0) {
    return null;
  }

  const schema = combineSchemas(validationRules);

  try {
    schema.validateSync(value);
    return null;
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      return error.message;
    }
    return 'Validation error';
  }
}

/**
 * Validates an object against a schema object (for full form validation).
 * Similar to validate(data, constraints) in validate.js
 *
 * @param {object} data - The data object to validate
 * @param {object} constraints - Object mapping field names to validation rule arrays
 * @returns {object|null} - Object with field names as keys and error arrays as values, or null if valid
 */
export function validateObject(data, constraints) {
  const errors = {};
  let hasErrors = false;

  for (const [fieldName, rules] of Object.entries(constraints)) {
    const value = data[fieldName];
    const error = validateField(fieldName, value, rules);

    if (error) {
      errors[fieldName] = [error];
      hasErrors = true;
    }
  }

  return hasErrors ? errors : null;
}

// For backward compatibility, also export as RULES (same structure as before)
export const RULES = VALIDATION_SCHEMAS;

export default VALIDATION_SCHEMAS;

